/**
 * Basic Marketing Templates for Free Tier Users
 * These templates give free users a taste of what they can get with paid tiers
 */

export interface MarketingTemplate {
  id: string;
  name: string;
  category: 'salesPage' | 'email' | 'social';
  platform?: string;
  template: string;
  variables: string[]; // List of variables users can customize
}

export const marketingTemplates: MarketingTemplate[] = [
  // Sales Page Templates
  {
    id: 'sp-1',
    name: 'Problem-Solution Headline',
    category: 'salesPage',
    template: 'Transform [PROBLEM] into [DESIRED_OUTCOME] with [COURSE_NAME]',
    variables: ['PROBLEM', 'DESIRED_OUTCOME', 'COURSE_NAME']
  },
  {
    id: 'sp-2',
    name: 'Benefit-Driven Subheadline',
    category: 'salesPage',
    template: 'Learn the [SKILL] that [BENEFIT] - even if you\'re a complete beginner',
    variables: ['SKILL', 'BENEFIT']
  },

  // Email Subject Lines
  {
    id: 'email-1',
    name: 'Curiosity Gap',
    category: 'email',
    template: 'The #1 [TOPIC] mistake (and how to avoid it)',
    variables: ['TOPIC']
  },
  {
    id: 'email-2',
    name: 'Direct Benefit',
    category: 'email',
    template: 'How to [ACHIEVE_GOAL] in [TIME_FRAME]',
    variables: ['ACHIEVE_GOAL', 'TIME_FRAME']
  },

  // Social Media Posts
  {
    id: 'social-1',
    name: 'Educational Hook',
    category: 'social',
    platform: 'Instagram/Facebook',
    template: `🎯 Want to master [SKILL]?

Here are 3 quick tips to get started:

1️⃣ [TIP_1]
2️⃣ [TIP_2]
3️⃣ [TIP_3]

💡 Learn more in my new course: [COURSE_NAME]

#[HASHTAG] #onlinelearning #courselaunch`,
    variables: ['SKILL', 'TIP_1', 'TIP_2', 'TIP_3', 'COURSE_NAME', 'HASHTAG']
  },
  {
    id: 'social-2',
    name: 'Problem-Agitation Post',
    category: 'social',
    platform: 'Twitter/X',
    template: `Struggling with [PROBLEM]?

You're not alone.

[COURSE_NAME] shows you exactly how to [SOLUTION] - step by step.

🔗 [LINK]`,
    variables: ['PROBLEM', 'COURSE_NAME', 'SOLUTION', 'LINK']
  }
];

// Helper function to get templates by category
export const getTemplatesByCategory = (category: 'salesPage' | 'email' | 'social'): MarketingTemplate[] => {
  return marketingTemplates.filter(t => t.category === category);
};

// Helper function to fill in template variables
export const fillTemplate = (template: MarketingTemplate, values: Record<string, string>): string => {
  let filled = template.template;
  template.variables.forEach(variable => {
    const value = values[variable] || `[${variable}]`;
    filled = filled.replace(new RegExp(`\\[${variable}\\]`, 'g'), value);
  });
  return filled;
};
